import {
  FC, KeyboardEvent, ReactNode, createContext, memo, useContext, useEffect, useId, useRef, useState,
} from 'react';

import * as Style from '../../styles/context/styles.modal';
import { useEventContext } from './event.context';
import { useI18nContext } from './i18n.context';
import { useLoaderScreenContext } from './loader-screen.context';
import { useMembershipContext } from './membership.context';

export interface ModalContextProps {
    setContentModal: (content: React.ReactNode) => void;
    toggleModal: ({ open }: {open: boolean}) => void;
    defineTitleModal: (title: string) => void;
    handleCustomSizeModal: ({ width, height }: {width: number, height: number}) => void;
    showModal: boolean;
    modalIsOpen: boolean;
    shouldRender: boolean;
    titleModal: string | null;
}

const ModalContext = createContext({} as ModalContextProps);

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Keeps Tab and Shift+Tab cycling inside the dialog. The overlay is rendered before the page in
 * the DOM, so without this the next Tab would leave the dialog for the page behind it.
 */
const keepFocusInside = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Tab') return;
  const dialog = event.currentTarget;
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (!first) {
    event.preventDefault();
  } else if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const ModalProvider: FC<{children: ReactNode}> = memo(({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [titleModal, setTitleModal] = useState<string | null>(null);
  const [childrenModal, setChildrenModal] = useState<ReactNode>(null);
  const [customSizeModal, setCustomSizeModal] = useState<{width: number, height: number} | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  // Two providers can be mounted at once (app shell and layout), so the title id must be unique.
  const titleId = useId();

  const { translate } = useI18nContext();
  const { couponApplied } = useEventContext();
  const { fetchingMembership } = useMembershipContext();
  const { showLoader } = useLoaderScreenContext();

  function handleCustomSizeModal({ width, height }: {width: number, height: number}) {
    setCustomSizeModal({ width, height });
  }

  /**
   * Toggles the visibility of the modal.
  */
  const toggleModal = ({ open }: {open: boolean}) => {
    if ((fetchingMembership || showLoader) && showModal && !open) return;
    setShowModal(open);
  };

  /**
   * Sets the content of the modal.
   * @param content - The content to be displayed in the modal.
  */
  const setContentModal = (content: ReactNode) => setChildrenModal(content);

  /**
   * Defines the title of the modal.
   * @param title - The title to be set for the modal.
  */
  const defineTitleModal = (title: string) => setTitleModal(title);

  const values: ModalContextProps = {
    setContentModal, toggleModal, defineTitleModal, handleCustomSizeModal, showModal,
    modalIsOpen : showModal, shouldRender, titleModal
  };

  /**
   * useEffect hook to control the rendering of the modal based on its visibility.
   * This hook runs whenever the `showModal` state changes and performs the following actions:
   * - If the modal is shown (`showModal` is true), it sets `shouldRender` to true immediately.
   * - If the modal is hidden (`showModal` is false), it sets a timeout of 480 milliseconds to:
   *   - Set `shouldRender` to false.
   *   - Clear the content of the modal (`setChildrenModal(null)`).
   *   - Clear the title of the modal (`setTitleModal(null)`).
   * The timeout ensures a smooth transition before completely unmounting the modal content.
   * Reopening before it fires cancels it, otherwise it would hide the modal that just reopened
   * and wipe its new title and content.
   * @param showModal - The boolean state indicating whether the modal is visible or not.
  */
  useEffect(() => {
    if (showModal) {
      setShouldRender(true);
      return undefined;
    }
    const unmountTimer = setTimeout(() => {
      setShouldRender(false);
      setChildrenModal(null);
      setTitleModal(null);
    }, 480);
    const resetSizeTimer = setTimeout(() => {
      setCustomSizeModal(null);
    }, 500);

    return () => {
      clearTimeout(unmountTimer);
      clearTimeout(resetSizeTimer);
    };
  }, [showModal]);

  /*
   * Takes focus back to whatever opened the modal (the "?" of the fees, a button...) once it
   * closes, so keyboard and screen-reader users continue where they were.
   */
  useEffect(() => {
    if (!showModal) return undefined;
    const trigger = document.activeElement;
    return () => {
      if (trigger instanceof HTMLElement) trigger.focus();
    };
  }, [showModal]);

  // Moves focus into the dialog once it is actually rendered, so it is announced on open.
  useEffect(() => {
    if (showModal && shouldRender) dialogRef.current?.focus();
  }, [showModal, shouldRender]);

  /**
   * useEffect hook to handle the 'Escape' key press to close the modal.
   * This hook runs whenever the `toggleModalInfo` function changes and performs the following actions:
   * - Adds an event listener to the document for the 'keydown' event.
   * - The event listener checks if the pressed key is 'Escape' and if the modal is shown (`showModal`).
   *   - If both conditions are met, it calls the `toggleModalInfo` function to toggle the modal visibility.
   * - Cleans up the event listener when the component unmounts or when `toggleModalInfo` changes.
   * @param toggleModalInfo - The function to toggle the visibility of the modal.
  */
  useEffect(() => {
    const handleKeyDown = (event: { key: string; }) =>
      (event.key === 'Escape' && showModal) && toggleModal({ open : !showModal });
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleModal]);

  return(
    <ModalContext.Provider value={values}>
      <Style.StyledWrapperModal
        $isVisible={showModal}
        $shouldRender={shouldRender}
      >
        <Style.StyledContentModal
          id="modal-content"
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleModal ? titleId : undefined}
          tabIndex={-1}
          onKeyDown={keepFocusInside}
          $isVisible={showModal}
          $extendsBottomPadding={couponApplied?.isValid}
          $customSizeModal={customSizeModal}
        >

          {
            titleModal && (
              <Style.StyledHeaderModal>
                <Style.StyledTitleModal id={titleId}>{ titleModal }</Style.StyledTitleModal>
                <Style.StyledCloseButton
                  type="button"
                  aria-label={translate(['MODAL', 'CLOSE'])}
                  onClick={() => toggleModal({ open : false })}
                >
                  <Style.StyledCloseModal aria-hidden="true" />
                </Style.StyledCloseButton>
              </Style.StyledHeaderModal>
            )
          }

          <Style.StyledModalBody>
            { childrenModal }
          </Style.StyledModalBody>

        </Style.StyledContentModal>
      </Style.StyledWrapperModal>
      {children}
    </ModalContext.Provider>
  );

});

ModalProvider.displayName = 'ModalProvider';
export const useModalContext = (): ModalContextProps => useContext(ModalContext);

export { ModalContext, ModalProvider };
